import React from "react";
import Avatar from "../components/basic/avatar";
import PopoverMenu from "../components/basic/popover-menu";
import type {
  AccountRow,
  BotConfig,
  BulkAccountInput,
  SettingsForm,
} from "../params/types";

export const SUCCESS_DISMISS_MS = 4500;

export function formatRelative(iso?: string | null) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const sec = Math.round((Date.now() - t) / 1000);
  if (sec < 10) return "just now";
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.round(hr / 24);
  return `${days}d ago`;
}

export function isErrorLogLine(line?: string | null) {
  return /\b(error|failed|exception|fatal|econnrefused|timeout|challenge)\b/i.test(
    line || "",
  );
}

/** Instagram suspended / disabled / removed accounts are stored as banned. */
export function isSuspendedAccount(
  account?: { session_status?: string | null } | null,
): boolean {
  return String(account?.session_status || "").toLowerCase() === "banned";
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** True when text names a hidden account by login or Instagram handle. */
export function mentionsHiddenAccount(
  text: string | null | undefined,
  hidden: ReadonlyArray<{
    username?: string | null;
    ig_username?: string | null;
  }>,
): boolean {
  const hay = String(text || "");
  if (!hay || !hidden.length) return false;
  const keys = hidden
    .flatMap((account) => [account.username, account.ig_username])
    .map((value) => String(value || "").replace(/^@/, "").trim())
    .filter((value) => value.length >= 3);
  if (!keys.length) return false;
  const pattern = new RegExp(
    `(?:^|[^a-z0-9._])(?:${keys.map(escapeRegExp).join("|")})(?:[^a-z0-9._]|$)`,
    "i",
  );
  return pattern.test(hay);
}

export function accountKeyHidden(
  account: string | null | undefined,
  hidden: ReadonlyArray<{ username?: string | null }>,
): boolean {
  if (!account || !hidden.length) return false;
  return hidden.some((row) => row.username === account);
}

/** True when Info tab should offer Log in instead of Log out. */
export function accountNeedsLogin(account?: AccountRow | null): boolean {
  if (!account) return false;
  const st = String(account.session_status || "").toLowerCase();
  if (st === "banned") return false;
  if (st === "needs_login" || st === "need_login" || st === "challenge") {
    return true;
  }
  if (account.has_session === false) return true;
  if (st === "ok") return false;
  return account.has_session !== true;
}

export function listToText(arr?: string[] | null) {
  return Array.isArray(arr) ? arr.join("\n") : "";
}

export function textToList(text?: string | null) {
  return String(text || "")
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export const StatusPill = ({
  status,
  running,
}: {
  status?: string | null;
  running?: boolean;
}) => {
  const raw = String(status || (running ? "RUNNING" : "STOPPED")).toUpperCase();
  const tone =
    raw === "RUNNING"
      ? "ok"
      : raw === "STOPPING" || raw === "STARTING"
        ? "warn"
        : raw === "NEED_CODE" || raw === "CODE_SENT"
          ? "warn"
          : "muted";
  return (
    <span className={`ib-pill ib-pill--${tone}`}>
      {raw.toLowerCase().replace(/_/g, " ")}
    </span>
  );
};

export const FlagPill = ({
  on,
  label,
  tone = "ok",
}: {
  on?: boolean;
  label: React.ReactNode;
  tone?: string;
}) => (
  <span className={`ib-pill ib-pill--${on ? tone : "muted"}`}>{label}</span>
);

export const sessionTone = (status?: string | null) => {
  const s = String(status || "unknown").toLowerCase();
  if (s === "ok") return "ok";
  if (s === "banned") return "danger";
  if (s === "needs_login" || s === "challenge") return "warn";
  return "muted";
};

export function accountInitials(name?: string | null) {
  const s = String(name || "?").replace(/^@/, "");
  return s.slice(0, 2).toUpperCase();
}

export function igHandleProfileUrl(handle?: string | null) {
  const h = String(handle || "")
    .replace(/^@/, "")
    .trim();
  if (!h || !/^[A-Za-z0-9._]+$/.test(h)) return null;
  return `https://www.instagram.com/${encodeURIComponent(h)}/`;
}

export function igProfileUrl(account?: AccountRow | null) {
  const raw =
    account?.ig_username ||
    (String(account?.username || "").includes("@") ? null : account?.username);
  return igHandleProfileUrl(raw);
}

export const ACCOUNT_AVATAR_SIZE = 72;

export function ProfileAvatar({
  url,
  text,
  image,
  size = ACCOUNT_AVATAR_SIZE,
}: {
  url?: string | null;
  text?: string | null;
  image?: string | null;
  size?: number;
}) {
  const avatar = (
    <Avatar text={text} image={image} width={size} height={size} colorful />
  );
  if (!url) return avatar;
  const label = text
    ? `Open Instagram profile for ${text}`
    : "Open Instagram profile";
  return (
    <a
      className="ib-profile-avatar"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title={label}
      aria-label={label}
    >
      {avatar}
    </a>
  );
}

export function igHandleLabel(account?: AccountRow | null) {
  if (account?.ig_username) {
    return `@${String(account.ig_username).replace(/^@/, "")}`;
  }
  const u = String(account?.username || "").replace(/^@/, "");
  if (u && !u.includes("@") && /^[A-Za-z0-9._]+$/.test(u)) return `@${u}`;
  return null;
}

export const challengeChannelHint = (channel?: string | null) => {
  const c = String(channel || "").toUpperCase();
  if (c === "TOTP") return "authenticator app";
  if (c === "EMAIL") return "account email inbox";
  if (c === "WHATSAPP") return "WhatsApp";
  if (c === "SMS") return "SMS / texts";
  if (c === "CHECKPOINT") return "email, SMS, or app shown by Instagram";
  return null;
};

export const SETTING_HELP: Record<string, string> = {
  poll_interval_sec:
    "Seconds to wait between full poll cycles (jittered slightly so timing is less robotic). Lower = more responsive, higher = safer.\n\nSuggested: 40–60. Minimum 15.",
  max_threads_per_cycle:
    "How many DM threads the bot opens for auto-replies in one cycle (per account). Keeps each run short and less spammy.\n\nSuggested: 3–5. Start at 4.",
  max_threads_per_day:
    "Unique chats the bot may message per account per UTC day (AI replies, welcomes, and queued DMs). Follow-ups in a chat already contacted today do not count extra.\n\nSuggested: 12–30.",
  max_messages_per_thread_per_day:
    "Max bot sends to the same contact per UTC day. Stops endless back-and-forth with one person. 0 = unlimited.\n\nSuggested: 20.",
  min_reply_gap_sec:
    "Minimum pause between two sends on the same account. Mimics human spacing.\n\nSuggested: 8–16 seconds. Default ~12.",
  min_minutes_before_reply:
    "After first seeing an inbound message, wait this many minutes before auto-replying. 0 = reply as soon as other rules allow.\n\nSuggested: 0 for active chats; 30–180 if you want a delayed feel.",
  quiet_hours_enabled:
    "When On, auto-replies and follower welcomes are suppressed during the quiet window. Admin-queued DMs still send.\n\nSuggested: On.",
  quiet_hours_start:
    "Local start of quiet hours (24h HH:MM). Uses Quiet TZ.\n\nSuggested: 22:00.",
  quiet_hours_end:
    "Local end of quiet hours (24h HH:MM). Window may wrap past midnight.\n\nSuggested: 08:00.",
  quiet_hours_tz:
    "IANA timezone for quiet hours (e.g. UTC, America/New_York, Europe/Berlin).\n\nSuggested: timezone of the account’s audience, or UTC.",
  followers_enabled:
    "When On, the bot scans new followers and can send a welcome DM (subject to quiet hours and caps).\n\nSuggested: On only if you want outreach; Off if DMs-only.",
  followers_follow_back:
    "When On, the bot follows a newcomer back only if a welcome DM will actually be sent that UTC day (same Welcomes / day cap). Pending or skipped people are not followed. Before the DM it opens their profile and clicks Follow / Follow Back if not already following.\n\nSuggested: On (natural reciprocal follow). Off if you only want to message without following.",
  followers_schedule:
    "When follow back and the welcome DM are queued.\n\nSoon (default): a few minutes apart, starting shortly.\nDelayed: spread across the rest of the active day. Choose this explicitly. Quiet hours still apply.",
  followers_max_per_cycle:
    "Max welcome DMs per account per UTC day. Keep low to avoid looking like a blast.\n\nSuggested: 10.",
  welcome_use_ai:
    "On: AI rewrites/generates welcome text (uses Welcome system prompt + optional template). Off: send the Welcome template as-is (or skip if empty).\n\nSuggested: On with a short template as seed.",
  skip_group_threads:
    "Skip threads that look like groups (titles with commas or “and”). Avoids bot replies in group chats.\n\nSuggested: Yes.",
  headless:
    "Deprecated global toggle — prefer Visible browser on each account / model. Run Chrome without a visible window. More detectable than headed.\n\nSuggested: Visible (headed) for high-value accounts.",
  visible_browser:
    "When On, show a real Chrome window. When Off, run headless (invisible) — more detectable.\n\nSuggested: On for high-value accounts.",
  browser_mode:
    "Desktop: multi-tab desktop Instagram web. Mobile: one shared mobile-emulated tab. Native: the Instagram Android app on the paired phone. The phone proxy is turned on and checked before Instagram work. Login, signup, inbox, followers, Reels warmup, and image publishing all run there. No Chrome for Instagram.\n\nRestart the account session after changing. Default: Desktop.",
  require_proxy:
    "When On, health warns if an account has no proxy. Launch still runs direct when unset — proxy is optional.\n\nSuggested: Off unless you want the reminder. Prefer sticky residential proxies for stealth.",
  slow_mo:
    "Extra milliseconds of Playwright slowMo (0–200). Hidden in UI; always 0 (human.js jitter is preferred).",
  ai_model:
    "OpenAI chat model id used for DM replies, the message-rule classifier, gallery pick, and AI welcomes.\n\nDefault: gpt-4o-mini. Choose any model from the account AI dropdown.",
  ai_temperature:
    "Creativity of AI replies (0 = deterministic, higher = more varied). Too high gets weird.\n\nSuggested: 0.6–0.9. Default 0.8.",
  alert_webhook_url:
    "Optional HTTPS URL for JSON POSTs on important events: 2FA/challenge, bans, process crash, stale challenge, failed-outbound spike, silent bot.\n\nSuggested: Slack/Discord incoming webhook. Leave blank to disable.",
  ignore_keywords:
    "If an inbound message contains any of these words/phrases (case-insensitive), skip auto-reply. Separate with commas or newlines.\n\nSuggested: spam, unsubscribe, stop — add terms you never want the bot to engage.",
  blocklist_usernames:
    "Global list — applies to every account. Never auto-reply, welcome, or follow-back these Instagram handles. Separate with commas or newlines. Also toggled via Ignore/Resume on the Followers page.",
  system_prompt:
    "System instructions for AI DM replies. Keep short, human, and on-brand. The bot still enforces length/safety elsewhere.\n\nSuggested: 2–5 sentences describing tone and what not to do.",
  welcome_prompt:
    "System instructions for AI welcome DMs to new followers.\n\nSuggested: warm, 1–2 sentences, no links/hashtags unless you really want them.",
  welcome_template:
    "Optional welcome text. Use {{username}} for the follower’s handle. With Welcome use AI On, this seeds the rewrite; with AI Off, this is sent verbatim.\n\nSuggested: “Hey {{username}}, thanks for the follow!”",
  media_replies_enabled:
    "When On, AI replies may attach an image/video from that account’s Gallery when the inbound message asks for media or a gallery category/description clearly fits. Never reuses the same file in the same thread. Global gallery is not used.\n\nSuggested: On after you upload items with clear categories/descriptions.",
  auto_accept_message_requests:
    "When On, the bot accepts Instagram Message Requests into the main inbox during periodic scans. When Off, requests are only logged/upserted for Inbox visibility.\n\nSuggested: Off (safer).",
  max_concurrent_sessions:
    "Max Chrome processes the continuous supervisor will run at once. Extra active accounts wait until a slot frees. Auto rotation never starts a group larger than this.\n\nSuggested: 10. Use 1 for debugging.",
  rotation_enabled:
    "When On, Start all runs one group of accounts, waits the shift time, stops that group, and starts the next. After the last group it starts over. Order is the order accounts were added, oldest first. Accounts you Stop stay out until Start all, or until you Start that account again.\n\nSave, then press Start all on Progress. Turning this off keeps the current group up; press Start all again to fill every free slot.",
  rotation_batch_size:
    "How many accounts run together in one rotation group. The last group can be smaller. Never exceeds Max concurrent sessions.\n\nSuggested: 5.",
  rotation_shift_minutes:
    "How long the current group stays open before the next group starts. The clock starts after that group has launched.\n\nSuggested: 30. Range: 1–1440.",
  hide_suspended_accounts:
    "When On, suspended accounts are left out of Accounts, Progress, Messages, Followers, Logs, and Export, including counts and banners. They stay saved. Turn this off to see them again.\n\nSave to apply.",
  browser_max_width:
    "Ceiling for the browser window width (CSS px) each profile rolls on first launch. Widths are picked from common monitor sizes (1280–1920) that fit under this cap, then persisted per account.\n\nLower it to keep windows smaller, then press “Recalculate viewports” to re-derive all accounts. Range: 640–3840.",
  browser_max_height:
    "Ceiling for the browser window height (CSS px) each profile rolls on first launch. Height follows the width ratio (≈0.55–0.72) and never exceeds this cap.\n\nPress “Recalculate viewports” to re-derive all accounts after changing it. Range: 640–3840.",
  ws_wake_enabled:
    "When On, Instagram chat signals schedule a contacts scan after a random Read timeout. Only chats with an unread badge are opened — other noise is ignored.\n\nSuggested: On.",
  ws_wake_delay_min_sec:
    "Minimum Read timeout (seconds) after a chat signal before scanning contacts for unread badges.\n\nSuggested: 4.",
  ws_wake_delay_max_sec:
    "Maximum Read timeout (seconds) after a chat signal before scanning unread contacts. Must be ≥ min.\n\nSuggested: 18.",
};

export const SettingHelp = ({ text }: { text?: string | null }) => {
  if (!text) return null;
  return (
    <PopoverMenu
      className="ib-setting-help"
      placement="top-center"
      menu={() => (
        <div className="ib-setting-help-body" role="dialog">
          {String(text)
            .split("\n\n")
            .map((para, i) => (
              <p key={i}>{para}</p>
            ))}
        </div>
      )}
    >
      <span className="ib-setting-help-btn" aria-label="Setting help">
        ?
      </span>
    </PopoverMenu>
  );
};

export const SettingField = ({
  helpKey,
  label,
  className = "",
  children,
}: {
  helpKey: string;
  label: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
}) => (
  <div className={`ib-setting-field ${className}`.trim()}>
    <div className="ib-setting-label">
      <span className="ib-setting-label-text">{label}</span>
      <SettingHelp text={SETTING_HELP[helpKey]} />
    </div>
    {children}
  </div>
);

export const TAB_DEFS = [
  {
    id: "accounts",
    label: "Accounts",
    icon: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z",
  },
  {
    id: "phones",
    label: "Phones",
    icon: "M8 2h8a2 2 0 012 2v16a2 2 0 01-2 2H8a2 2 0 01-2-2V4a2 2 0 012-2zM11 18h2",
  },
  {
    id: "models",
    label: "Models",
    icon: "M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z",
  },
  {
    id: "progress",
    label: "Progress",
    icon: "M3 3v18h18M7 15l4-4 3 3 6-7",
  },
  {
    id: "messages",
    label: "Messages",
    icon: "M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z",
  },
  {
    id: "followers",
    label: "Followers",
    icon: "M17 20h5v-2a4 4 0 00-3-3.87M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a4 4 0 117.288 0M13 7a4 4 0 11-8 0 4 4 0 018 0z",
  },
  {
    id: "migration",
    label: "Migration",
    icon: "M8 7h12m0 0l-4-4m4 4l-4 4M16 17H4m0 0l4-4m-4 4l4 4",
  },
  {
    id: "logs",
    label: "Logs",
    icon: "M9 6h11M9 12h11M9 18h11M4 6h.01M4 12h.01M4 18h.01",
  },
  {
    id: "settings",
    label: "Settings",
    icon: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.572-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.573-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z",
  },
];

export const TabIcon = ({ d, size = 19 }: { d: string; size?: number }) => (
  <svg
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path d={d} />
  </svg>
);

export function configToSettingsForm(
  config?: BotConfig | null,
): SettingsForm | null {
  if (!config) return null;
  return {
    max_concurrent_sessions: config.max_concurrent_sessions ?? 10,
    browser_max_width: config.browser_max_width ?? 1700,
    browser_max_height: config.browser_max_height ?? 900,
    rotation_enabled: config.rotation_enabled === true,
    rotation_batch_size: config.rotation_batch_size ?? 5,
    rotation_shift_minutes: config.rotation_shift_minutes ?? 30,
    hide_suspended_accounts: config.hide_suspended_accounts !== false,
    blocklist_usernames: listToText(config.blocklist_usernames),
  };
}

/**
 * DataImpulse sticky ports: rewrite `:10000` → `:10000 + ordinal`
 * so each account gets a distinct session port.
 */
export function adjustDataimpulseProxyPort(
  proxy: string,
  ordinal: number,
): string {
  const trimmed = String(proxy || "").trim();
  if (!trimmed) return trimmed;
  if (!/dataimpulse\.com:10000$/i.test(trimmed)) return trimmed;
  const n = Math.max(0, Math.floor(Number(ordinal) || 0));
  return trimmed.replace(/:10000$/i, `:${10000 + n}`);
}

export { isLiveModelId, pickLeastUsedModelId } from "../../../shared/domain/live-model";

/** Basic email shape — catches typos like `mikepolish66yahoo.com` (missing @). */
export function isEmailLike(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

/** Bare username/handle — letters, digits, dots, underscores, hyphens. */
export function isHandleLike(value: string): boolean {
  return /^[A-Za-z0-9._-]{2,64}$/.test(value);
}

/** Known mailbox domains — a login ending in one with no "@" is a dropped-@ typo. */
const KNOWN_MAILBOX_DOMAINS = [
  "gmail.com",
  "googlemail.com",
  "yahoo.com",
  "ymail.com",
  "rocketmail.com",
  "att.net",
  "verizon.net",
  "sbcglobal.net",
  "aol.com",
  "hotmail.com",
  "outlook.com",
  "live.com",
  "msn.com",
  "icloud.com",
  "me.com",
  "mac.com",
  "proton.me",
  "protonmail.com",
  "yandex.com",
  "mail.ru",
];

/** True when the login ends in a known mailbox domain but has no "@" (dropped-@ typo). */
export function looksLikeMissingAtEmail(value: string): boolean {
  const s = value.trim().toLowerCase();
  if (!s || s.includes("@")) return false;
  return KNOWN_MAILBOX_DOMAINS.some(
    (d) => s.endsWith(d) && s.length > d.length + 2,
  );
}

/**
 * Validate an account login line. Accepts a full email or a bare username;
 * returns an error message when neither works (or null when fine).
 */
export function validateLoginLine(username: string): string | null {
  if (!username) return "username is required";
  if (isEmailLike(username)) return null;
  if (looksLikeMissingAtEmail(username)) {
    return `"${username}" looks like an email with a missing "@" — did you mean user@domain.com?`;
  }
  if (isHandleLike(username)) return null;
  if (username.includes("@")) {
    return `"${username}" is not a valid email — check for typos like a missing "@" (expected user@domain.com)`;
  }
  return `"${username}" is not a valid username — use an email (user@domain.com) or letters/numbers/. _ -`;
}

/**
 * Parse bulk lines → upsert inputs.
 * Format: `email:password` or `email:password:proxyUrl`
 * Proxy is everything after the second `:`, kept intact (may contain `:`).
 */
export function parseBulkAccountLines(
  raw?: string | null,
  opts?: { proxyOrdinalStart?: number },
): {
  inputs: BulkAccountInput[];
  errors: string[];
} {
  const lines = String(raw || "")
    .split(/\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const inputs: BulkAccountInput[] = [];
  const errors: string[] = [];
  const rawStart = Number(opts?.proxyOrdinalStart);
  const ordinalStart = Number.isFinite(rawStart)
    ? Math.max(0, Math.floor(rawStart))
    : 0;
  lines.forEach((line, idx) => {
    const first = line.indexOf(":");
    if (first < 0) {
      errors.push(`Line ${idx + 1}: need email:password[:proxy]`);
      return;
    }
    const second = line.indexOf(":", first + 1);
    const username = line.slice(0, first).trim();
    const password =
      second < 0
        ? line.slice(first + 1).trim()
        : line.slice(first + 1, second).trim();
    const proxyRaw = second < 0 ? "" : line.slice(second + 1).trim();
    if (!username || !password) {
      errors.push(`Line ${idx + 1}: email and password required`);
      return;
    }
    const loginError = validateLoginLine(username);
    if (loginError) {
      errors.push(`Line ${idx + 1}: ${loginError}`);
      return;
    }
    let proxy: string | null = proxyRaw || null;
    if (proxy) {
      const ordinal = ordinalStart + inputs.length;
      proxy = adjustDataimpulseProxyPort(proxy, ordinal);
    }
    inputs.push({
      username,
      password,
      first_name: null,
      last_name: null,
      proxy,
    });
  });
  return { inputs, errors };
}
